import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import {
  ArrowDownToLine,
  ArrowLeftRight,
  ArrowUpFromLine,
  CircleDollarSign,
  LayoutDashboard,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  TrendingUp,
} from 'lucide-react'
import { useAuth, useMe } from '@/lib/auth'
import { primaryWallet, type Me } from '@/lib/api-types'
import { useBalanceWatcher } from '@/lib/portfolio'
import { Avatar } from './avatar'
import { NotificationBell } from './notification-bell'
import { ActivityBar } from './activity-bar'

const NAV = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/strategies', label: 'Strategies', icon: TrendingUp },
  { to: '/pnl', label: 'P&L', icon: CircleDollarSign },
  { to: '/settings', label: 'Settings', icon: Settings },
]

// Swap, Deposit and Withdraw open their dialogs on the dashboard, from any page.
const ACTIONS = [
  { to: '/dashboard?action=swap', label: 'Swap', icon: ArrowLeftRight },
  { to: '/dashboard?action=deposit', label: 'Deposit', icon: ArrowDownToLine },
  { to: '/dashboard?action=withdraw', label: 'Withdraw', icon: ArrowUpFromLine },
]

// Remembered per browser; a convenience, so storage failures just mean "expanded".
const COLLAPSED_KEY = 'dispence.sidebar-collapsed'

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

function writeCollapsed(value: boolean) {
  try {
    window.localStorage.setItem(COLLAPSED_KEY, value ? '1' : '0')
  } catch {
    // private mode or blocked storage: the choice lasts for this tab
  }
}

const linkClass = (active: boolean, collapsed: boolean) =>
  `relative flex items-center gap-3 rounded-control py-2.5 text-base font-medium transition-colors ${
    collapsed ? 'justify-center px-0' : 'px-3'
  } ${
    active
      ? 'bg-surface-2 text-text before:absolute before:left-0 before:top-1/2 before:h-6 before:w-0.5 before:-translate-y-1/2 before:rounded-full before:bg-accent'
      : 'text-muted hover:bg-surface hover:text-text'
  }`

export function AppLayout() {
  const { signOut } = useAuth()
  const { user } = useMe()
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(readCollapsed)
  useBalanceWatcher(user?.walletSetupComplete ? primaryWallet(user) : null)
  const title = NAV.find((n) => location.pathname.startsWith(n.to))?.label ?? 'Dispence'
  function toggleSidebar() {
    setCollapsed((value) => {
      writeCollapsed(!value)
      return !value
    })
  }
  return (
    <div className="flex h-screen overflow-hidden">
      <aside
        className={`hidden shrink-0 flex-col border-r border-line bg-bg py-4 transition-[width] duration-200 ease-out md:flex ${
          collapsed ? 'w-16 px-2' : 'w-52 px-3'
        }`}
      >
        <div className={`flex items-center pb-4 ${collapsed ? 'flex-col gap-2' : 'justify-between px-2'}`}>
          <Link to="/dashboard" className="shrink-0 rounded-lg focus-visible:outline-2 focus-visible:outline-accent" title="Dashboard" aria-label="Dashboard">
            <img src="/brand/logo-tile.png" alt="" className="size-10 rounded-lg" />
          </Link>
          <button
            type="button"
            className="btn btn-ghost !p-2"
            onClick={toggleSidebar}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
          >
            {collapsed ? <PanelLeftOpen className="size-5" /> : <PanelLeftClose className="size-5" />}
          </button>
        </div>
        <nav className="flex flex-col gap-1">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} className={({ isActive }) => linkClass(isActive, collapsed)} title={label} aria-label={label}>
              <Icon className="size-5 shrink-0" />
              {collapsed ? null : <span className="truncate">{label}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="my-4 border-t border-line" />
        <div className="flex flex-col gap-1">
          {ACTIONS.map(({ to, label, icon: Icon }) => (
            <Link key={label} to={to} className={linkClass(false, collapsed)} title={label} aria-label={label}>
              <Icon className="size-5 shrink-0" />
              {collapsed ? null : <span className="truncate">{label}</span>}
            </Link>
          ))}
        </div>
        <div className="mt-auto pt-4">
          <AccountRow user={user} collapsed={collapsed} onSignOut={signOut} />
        </div>
      </aside>
      <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-line px-4 sm:h-16 sm:px-6 lg:px-8">
          {/* phones: the pages, the actions and the account behind one menu at the top left; the bottom tabs covered the
              activity bar (John, Nov 1 2026) */}
          <MobileMenu user={user} onSignOut={signOut} />
          <h1 className="text-lg font-semibold sm:text-xl">{title}</h1>
          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <NotificationBell />
          </div>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <div className="mx-auto w-full max-w-6xl px-4 py-4 sm:px-6 sm:py-6 lg:px-8">
            <Outlet />
          </div>
        </div>
        <ActivityBar />
      </main>
    </div>
  )
}


/**
 * The signed-in account at the foot of the sidebar: photo, name and email,
 * with Sign out appearing on hover. Collapsed, the photo opens a small menu
 * with the same two things.
 */
function AccountRow({ user, collapsed, onSignOut }: { user: Me | null | undefined; collapsed: boolean; onSignOut: () => void }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])
  useEffect(() => {
    if (!collapsed) setOpen(false)
  }, [collapsed])
  const name = user?.name ?? user?.email ?? user?.telegramUsername ?? 'Signed in'
  // the email is a second line only when the first line is a name
  const email = user?.name ? user.email : null
  if (collapsed) {
    return (
      <div ref={ref} className="relative flex justify-center">
        <button
          type="button"
          className="rounded-full transition-opacity hover:opacity-80 focus-visible:outline-2 focus-visible:outline-accent"
          onClick={() => setOpen((value) => !value)}
          title={name}
          aria-label="Account"
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <Avatar user={user} className="size-10" />
        </button>
        {open ? (
          <div role="menu" className="card absolute bottom-0 left-full z-50 ml-3 w-56 p-2 shadow-lg shadow-black/40">
            <div className="flex items-center gap-3 px-2 py-1.5">
              <Avatar user={user} className="size-10" />
              <div className="min-w-0 text-sm">
                <div className="truncate font-medium">{name}</div>
                {email ? <div className="truncate text-muted">{email}</div> : null}
              </div>
            </div>
            <button type="button" role="menuitem" className="btn btn-ghost mt-1 w-full !justify-start !px-2 !py-2 text-base" onClick={onSignOut}>
              <LogOut className="size-5" /> Sign out
            </button>
          </div>
        ) : null}
      </div>
    )
  }
  return (
    <div className="group flex items-center gap-3 rounded-control px-2 py-2 transition-colors hover:bg-surface">
      <Avatar user={user} className="size-10" />
      <div className="min-w-0 flex-1 text-sm">
        <div className="truncate font-medium">{name}</div>
        {email ? <div className="truncate text-muted">{email}</div> : null}
      </div>
      <button
        type="button"
        onClick={onSignOut}
        className="btn btn-ghost !p-2 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
        title="Sign out"
        aria-label="Sign out"
      >
        <LogOut className="size-5" />
      </button>
    </div>
  )
}

/** The phone's navigation: one button at the top left opening the pages, the actions and the account. */
function MobileMenu({ user, onSignOut }: { user: Me | null | undefined; onSignOut: () => void }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const location = useLocation()
  useEffect(() => {
    setOpen(false)
  }, [location.pathname, location.search])
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])
  const name = user?.name ?? user?.email ?? user?.telegramUsername ?? 'Signed in'
  const email = user?.name ? user.email : null
  return (
    <div ref={ref} className="relative shrink-0 md:hidden">
      <button type="button" className="btn btn-ghost !p-2" onClick={() => setOpen((value) => !value)} aria-label="Menu" aria-haspopup="menu" aria-expanded={open}>
        <Menu className="size-6" />
      </button>
      {open ? (
        <div role="menu" className="card absolute left-0 top-full z-50 mt-2 w-64 p-2 shadow-lg shadow-black/40">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar user={user} className="size-9" />
            <div className="min-w-0 text-sm">
              <div className="truncate font-medium">{name}</div>
              {email ? <div className="truncate text-muted">{email}</div> : null}
            </div>
          </div>
          <div className="my-1 border-t border-line" />
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} role="menuitem" className={({ isActive }) => `btn btn-ghost w-full !justify-start !px-2 !py-2 text-base ${isActive ? 'text-accent' : ''}`}>
              <Icon className="size-5" /> {label}
            </NavLink>
          ))}
          <div className="my-1 border-t border-line" />
          {ACTIONS.map(({ to, label, icon: Icon }) => (
            <Link key={label} to={to} role="menuitem" className="btn btn-ghost w-full !justify-start !px-2 !py-2 text-base">
              <Icon className="size-5" /> {label}
            </Link>
          ))}
          <div className="my-1 border-t border-line" />
          <button type="button" role="menuitem" className="btn btn-ghost w-full !justify-start !px-2 !py-2 text-base" onClick={onSignOut}>
            <LogOut className="size-5" /> Sign out
          </button>
        </div>
      ) : null}
    </div>
  )
}
